import React, { useState } from 'react';
import { cn } from '../../utils/cn';
import { Avatar } from '../../primitives/Avatar/Avatar';
import { Badge, BadgeGroup } from '../../primitives/Badge/Badge';
import { Button, IconButton } from '../../primitives/Button/Button';
import { Card, CardContent, CardSection, CardFooter } from '../Card/Card';

/**
 * ContactCard Component
 *
 * Contact card for displaying journalists, broadcasters, and podcasters.
 * Uses standard Tailwind classes for maximum compatibility.
 */

// Icons
const ExternalLinkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const MailIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const PhoneIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const TwitterIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkedInIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const LockIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const MicIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

const TvIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const StarIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const CopyIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

/**
 * Get contact type icon
 */
function getContactTypeIcon(type) {
  switch (type) {
    case 'podcast':
      return MicIcon;
    case 'broadcaster':
      return TvIcon;
    default:
      return null;
  }
}

export function ContactCard({
  contact,
  type = 'journalist',
  locked = false,
  onAddToList,
  addToListComponent,
  onEmail,
  onViewProfile,
  className,
  style,
  ...props
}) {
  const [showAllTags, setShowAllTags] = useState(false);
  const [copyState, setCopyState] = useState({ email: null, phone: null });
  const {
    name,
    avatar,
    outlet,
    outlets = [],
    title,
    role,
    bio,
    beats = [],
    topics = [],
    email,
    phone,
    twitter,
    linkedin,
    website,
    country,
    countryFlag,
    rating,
    episodes,
    followers,
  } = contact;

  const displayOutlet = outlet || (outlets.length > 0 ? outlets.join(', ') : null);
  const displayRole = title || role;
  const TypeIcon = getContactTypeIcon(type);
  const totalTags = beats.length + topics.length;
  const visibleBeats = showAllTags ? beats : beats.slice(0, 3);
  const visibleTopics = showAllTags ? topics : topics.slice(0, 2);
  const remainingTags = totalTags - (visibleBeats.length + visibleTopics.length);

  const copyToClipboard = async (value, field) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopyState((prev) => ({ ...prev, [field]: 'success' }));
      setTimeout(() => {
        setCopyState((prev) => ({ ...prev, [field]: null }));
      }, 1200);
    } catch (err) {
      console.error('Copy failed', err);
      setCopyState((prev) => ({ ...prev, [field]: 'error' }));
      setTimeout(() => {
        setCopyState((prev) => ({ ...prev, [field]: null }));
      }, 1200);
    }
  };

  return (
    <Card
      hoverable
      className={cn('group', addToListComponent && 'overflow-visible', className)}
      style={style}
      {...props}
    >
      {/* Main Content */}
      <CardContent padding="default">
        <div className="flex items-start gap-4">
          {/* Avatar with badges */}
          <div className="relative flex-shrink-0">
            <Avatar
              src={avatar}
              name={name}
              size="xl"
              className="ring-2 ring-gray-100 group-hover:ring-blue-100 transition-all duration-200"
            />

            {/* Country flag badge */}
            {countryFlag && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full overflow-hidden border-2 border-white shadow-sm">
                <img
                  src={countryFlag}
                  alt={country}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Type icon badge */}
            {TypeIcon && (
              <div className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center border-2 border-white shadow-sm">
                <TypeIcon className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          {/* Name, outlet, role */}
          <div className="flex-1 min-w-0">
            {/* Name */}
            <div className="flex items-start gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900 leading-tight group-hover:text-blue-700 transition-colors duration-150">
                {name}
              </h3>

              {/* Website link pill */}
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-blue-50 rounded-full text-xs font-medium text-gray-500 hover:text-blue-600 transition-colors duration-150"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLinkIcon className="w-3 h-3" />
                  <span className="max-w-[80px] truncate">
                    {new URL(website).hostname.replace('www.', '')}
                  </span>
                </a>
              )}
            </div>

            {/* Role and Outlet - FIXED: proper spacing */}
            <div className="mt-1.5">
              {displayRole && displayOutlet ? (
                <p className="text-sm text-gray-600 leading-snug">
                  <span className="font-medium">{displayRole}</span>
                  <span className="text-gray-400 mx-1">at</span>
                  <span className="font-semibold text-gray-800">{displayOutlet}</span>
                </p>
              ) : displayRole ? (
                <p className="text-sm font-medium text-gray-600">{displayRole}</p>
              ) : displayOutlet ? (
                <p className="text-sm font-semibold text-gray-800">{displayOutlet}</p>
              ) : null}
            </div>

            {/* Podcast stats */}
            {type === 'podcast' && (rating || episodes || followers) && (
              <div className="flex items-center gap-4 mt-2">
                {rating && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-xs font-semibold text-amber-700">
                    <StarIcon className="w-3.5 h-3.5 text-amber-500" />
                    {rating.toFixed(1)}
                  </span>
                )}
                {episodes && (
                  <span className="text-xs font-medium text-gray-500">
                    {episodes.toLocaleString()} episodes
                  </span>
                )}
                {followers && (
                  <span className="text-xs font-medium text-gray-500">
                    {followers.toLocaleString()} followers
                  </span>
                )}
              </div>
            )}

            {/* Social links - shown on hover */}
            <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {twitter && (
                <a
                  href={`https://twitter.com/${twitter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-sky-500 hover:bg-sky-50 transition-colors duration-150"
                  onClick={(e) => e.stopPropagation()}
                >
                  <TwitterIcon className="w-4 h-4" />
                </a>
              )}
              {linkedin && (
                <a
                  href={linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-150"
                  onClick={(e) => e.stopPropagation()}
                >
                  <LinkedInIcon className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Bio */}
        {bio && (
          <p className="mt-4 text-sm text-gray-600 leading-relaxed line-clamp-2">
            {bio}
          </p>
        )}
      </CardContent>

      {/* Tags Section */}
      {(beats.length > 0 || topics.length > 0) && (
        <CardSection>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Covers
          </div>
          <BadgeGroup>
            {visibleBeats.map((beat, index) => (
              <Badge key={`beat-${index}`} variant="beat" size="sm">
                {beat}
              </Badge>
            ))}
            {visibleTopics.map((topic, index) => (
              <Badge key={`topic-${index}`} variant="topic" size="sm">
                {topic}
              </Badge>
            ))}
            {remainingTags > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllTags(true);
                }}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                +{remainingTags} more
              </button>
            )}
          </BadgeGroup>
        </CardSection>
      )}

      {/* Footer with actions */}
      <CardFooter align="between">
        <div className="flex items-center gap-2">
          {locked ? (
            <Button
              variant="secondary"
              size="sm"
              icon={LockIcon}
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = '/pricing';
              }}
            >
              Unlock Contact
            </Button>
          ) : (
            <div className="flex items-center gap-1.5">
              {email && (
                <div className="flex items-center gap-1.5 max-w-[240px]">
                  <a
                    href={`mailto:${email}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors truncate"
                  >
                    <MailIcon className="w-4 h-4" />
                    <span className="truncate">{email}</span>
                  </a>
                  <IconButton
                    icon={copyState.email === 'success' ? CheckIcon : CopyIcon}
                    variant="secondary"
                    size="sm"
                    label="Copy email"
                    className={copyState.email === 'success' ? 'text-green-600' : copyState.email === 'error' ? 'text-red-600' : ''}
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(email, 'email');
                    }}
                  />
                </div>
              )}
              {phone && (
                <div className="flex items-center gap-1.5">
                  <a
                    href={`tel:${phone}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <PhoneIcon className="w-4 h-4" />
                    <span className="truncate">{phone}</span>
                  </a>
                  <IconButton
                    icon={copyState.phone === 'success' ? CheckIcon : CopyIcon}
                    variant="secondary"
                    size="sm"
                    label="Copy phone"
                    className={copyState.phone === 'success' ? 'text-green-600' : copyState.phone === 'error' ? 'text-red-600' : ''}
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(phone, 'phone');
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add to list button */}
        {addToListComponent ? (
          <div onClick={(e) => e.stopPropagation()}>{addToListComponent}</div>
        ) : (
          <Button
            variant="primary"
            size="sm"
            icon={PlusIcon}
            onClick={(e) => {
              e.stopPropagation();
              if (onAddToList) onAddToList(contact);
            }}
          >
            Add to List
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

/**
 * Locked Contact Card - For trial users
 */
export function LockedContactCard({
  contact,
  type = 'journalist',
  onUpgrade,
  className,
  ...props
}) {
  return (
    <ContactCard
      contact={contact}
      type={type}
      locked
      className={cn('relative overflow-hidden', className)}
      {...props}
    />
  );
}

export default ContactCard;
